import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  batchHistoryDetailService,
  BatchDetailsItem,
  BatchDetailsParams,
} from "../services/batchHistoryDetailService";
import Header from "./Header";
import BackNav from "./BackNav";
import Loading from "./Loading";
import "./BatchHistoryDetail.css";
import { beautifyJsonStringsIn } from "../../utils/beautifyErrorMsg";

interface BatchHistoryDetailProps {
  user: any;
  onLogout: () => void;
}

interface PaginationInfo {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

const BatchHistoryDetail: React.FC<BatchHistoryDetailProps> = ({
  user,
  onLogout,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [batchDetails, setBatchDetails] = useState<BatchDetailsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    perPage: 25,
    total: 0,
    totalPages: 0,
  });

  // Expanded error message state
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());

  // Ref to prevent duplicate API calls
  const isLoadingRef = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL parameters on component mount
  useEffect(() => {
    if (!id || isNaN(Number(id))) {
      navigate("/batch_history");
      return;
    }

    const page = parseInt(searchParams.get("page") || "1");

    setPagination(prev => ({
      ...prev,
      page: page,
    }));

    loadBatchDetails(page);
  }, [id, navigate, searchParams.get("page")]);

  const updateURL = (page: number) => {
    const params = new URLSearchParams();
    if (page > 1) {
      params.set("page", page.toString());
    }
    setSearchParams(params);
  };

  const loadBatchDetails = async (page: number = 1) => {
    // Prevent duplicate API calls
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      const params: BatchDetailsParams = {
        batchId: Number(id!),
        page,
        perPage: pagination.perPage,
      };

      const response = await batchHistoryDetailService.getBatchDetails(params);
      if (response.success) {
        setBatchDetails(response.data);
        setPagination({
          page: response.pagination.page,
          perPage: response.pagination.perPage,
          total: parseInt(response.pagination.total),
          totalPages: response.pagination.totalPages,
        });
      } else {
        setError(response.message || "連携ログ詳細の取得に失敗しました");
      }
    } catch (err: any) {
      setError(err.message || "連携ログ詳細の取得に失敗しました");
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      updateURL(page);
      loadBatchDetails(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, pagination.page - 3);
    const endPage = Math.min(pagination.totalPages, pagination.page + 3);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const formatDate = (dateString: string | null) => {
    return batchHistoryDetailService.formatRequestDate(dateString);
  };

  const getStatusDisplayText = (status: string | null) => {
    return batchHistoryDetailService.getStatusDisplayText(status);
  };

  const getStatusClass = (status: string | null) => {
    return batchHistoryDetailService.getStatusClass(status);
  };

  const toggleErrorExpansion = (documentId: number) => {
    const newExpanded = new Set(expandedErrors);
    if (newExpanded.has(documentId)) {
      newExpanded.delete(documentId);
    } else {
      newExpanded.add(documentId);
    }
    setExpandedErrors(newExpanded);
  };

  const renderErrorMessages = (item: BatchDetailsItem) => {
    const errorMessages = batchHistoryDetailService.getAllErrorMessages(
      item.items
    );

    if (errorMessages.length === 0) {
      return (
        <div className="error-message-container no-error">
          <span className="error-text">-</span>
        </div>
      );
    }

    // Original full text from service
    const rawFullErrorText =
      batchHistoryDetailService.formatErrorMessages(errorMessages);

    // Replace JSON substrings with pretty-printed JSON strings
    const beautifiedFullText = beautifyJsonStringsIn(rawFullErrorText);

    const isExpanded = expandedErrors.has(item.documentId || 0);
    const isTooLong =
      batchHistoryDetailService.isErrorMessageTooLong(beautifiedFullText);

    const displayText = isExpanded
      ? beautifiedFullText
      : isTooLong
        ? batchHistoryDetailService.truncateErrorMessage(beautifiedFullText)
        : beautifiedFullText;

    return (
      <div className="error-message-container">
        {/* Use pre to preserve pretty JSON formatting and newlines */}
        <pre className={`error-text ${isExpanded ? "expanded" : ""}`}>
          {displayText}
        </pre>
        {isTooLong && (
          <button
            className="error-toggle-button"
            onClick={() => toggleErrorExpansion(item.documentId || 0)}
            title={isExpanded ? "折りたたむ" : "展開"}
          >
            {isExpanded ? "▲" : "⋯"}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="batch-history-detail-container">
        <Loading message="連携ログ詳細を読み込み中..." fullHeight={true} />
      </div>
    );
  }

  return (
    <div className="batch-history-detail-container">
      {/* Header */}
      <Header user={user} onLogout={onLogout} />
      {/* Back Button (aligned with content) */}
      <BackNav label="連携ログに戻る" to="/batch_history" />

      {/* Content */}
      <div className="batch-history-detail-content">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">連携ログ詳細</h1>
          <p className="page-description">
            連携の実行履歴詳細を確認します
          </p>
        </div>

        {/* Separator */}
        <div className="separator"></div>

        {/* Table Section */}
        <div className="table-section">
          {error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button
                className="retry-button"
                onClick={() => loadBatchDetails(pagination.page)}
              >
                再試行
              </button>
            </div>
          ) : batchDetails.length === 0 ? (
            <div className="empty-container">
              <p className="empty-message">データがありません</p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="batch-history-detail-table">
                  <thead>
                    <tr>
                      <th className="document-number-column">文書番号</th>
                      <th className="request-date-column">申請日</th>
                      <th className="request-group-column">グループ</th>
                      <th className="request-user-column">ユーザー名</th>
                      <th className="form-name-column">申請書名</th>
                      <th className="processing-result-column">処理結果</th>
                      <th className="error-content-column">エラー内容</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchDetails.map((item, index) => (
                      <tr
                        key={item.documentId}
                        className={`batch-detail-row ${index % 2 === 0 ? "even" : "odd"}`}
                      >
                        <td className="document-number-cell">
                          {item.documentNumber || "-"}
                        </td>
                        <td className="request-date-cell">
                          {formatDate(item.requestDate)}
                        </td>
                        <td className="request-group-cell">
                          {item.requestGroup || "-"}
                        </td>
                        <td className="request-user-cell">
                          {item.requestUser || "-"}
                        </td>
                        <td className="form-name-cell">
                          {item.formName || "-"}
                        </td>
                        <td className="processing-result-cell">
                          <span
                            className={`status-badge ${getStatusClass(item.status)}`}
                          >
                            {getStatusDisplayText(item.status)}
                          </span>
                        </td>
                        <td className="error-content-cell">
                          {renderErrorMessages(item)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Pagination Section - Only show when there is data */}
        {batchDetails.length > 0 && (
          <div className="pagination-section">
            <div className="pagination-container">
              <div className="pagination-info">
                {(pagination.page - 1) * pagination.perPage + 1} -{" "}
                {Math.min(
                  pagination.page * pagination.perPage,
                  pagination.total
                )}{" "}
                / {pagination.total}
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                  title="最初のページ"
                >
                  «
                </button>
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  title="前のページ"
                >
                  ‹
                </button>
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    className={`pagination-button ${page === pagination.page ? "active" : ""}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  title="次のページ"
                >
                  ›
                </button>
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.page === pagination.totalPages}
                  title="最後のページ"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchHistoryDetail;
